import React, {useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {Building2, CreditCard, FileText, Pencil, Plus, QrCode, Search, Trash2, X} from "lucide-react";
import {RemoteSelect} from "../../../shared/ui";
import {getFinanceiroContaBancaria, listFinanceiroContasBancarias} from "../FinanceiroService";

const createInitialValues = (paymentMethod) => ({
    codigo: paymentMethod?.codigo || '',
    descricao: paymentMethod?.descricao || '',
    tipo: paymentMethod?.tipo || 'PIX',
    contaBancariaId: paymentMethod?.contaBancariaId || '',
    contaBancaria: paymentMethod?.contaBancaria || null,
    ativo: paymentMethod?.ativo ?? true,
});

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: '#fff',
    },
};

const sectionTitleSx = {fontSize: '1rem', color: '#374151', fontWeight: 600};

const paymentMethodTypes = [
    {value: 'PIX', label: 'PIX', icon: QrCode},
    {value: 'BOLETO', label: 'Boleto', icon: FileText},
    {value: 'CARTAO', label: 'Cartão', icon: CreditCard},
];

const getBankAccountLabel = (account) => {
    if (!account) return '';
    const name = account.apelido || account.nome_conta || account.nomeConta || 'Conta bancária';
    const bank = account.bancoNomeComercial || account.nomeBanco || account.banco || account.bancoNome || '';
    return bank ? `${name} - ${bank}` : name;
};

const getBankAccountDescription = (account) => {
    if (!account) return '';
    const agency = [account.agencia, account.agenciaDv].filter(Boolean).join('-');
    const checkingAccount = [account.conta, account.contaDv].filter(Boolean).join('-');
    if (!agency && !checkingAccount) return account.status || '';
    return `Ag: ${agency || '-'} / Cc: ${checkingAccount || '-'}`;
};

function PaymentMethodsDialog({paymentMethod, open, saving, onClose, onSave}) {
    const [values, setValues] = useState(createInitialValues(paymentMethod));
    const [errors, setErrors] = useState({});

    React.useEffect(() => {
        setValues(createInitialValues(paymentMethod));
        setErrors({});
    }, [paymentMethod, open]);

    // A API devolve só o id da conta: busca a conta para o seletor mostrar o nome dela.
    React.useEffect(() => {
        const contaBancariaId = paymentMethod?.contaBancariaId;
        if (!open || !contaBancariaId || paymentMethod?.contaBancaria) return undefined;

        let ativo = true;
        const preencher = (contaBancaria) => {
            if (!ativo) return;
            setValues((current) => (
                current.contaBancariaId === contaBancariaId && !current.contaBancaria ? {...current, contaBancaria} : current
            ));
        };
        getFinanceiroContaBancaria(contaBancariaId)
            .then((response) => {
                const conta = response?.data?.data ?? response?.data;
                preencher(conta?.id ? conta : {id: contaBancariaId, apelido: 'Conta bancária não encontrada'});
            })
            .catch(() => preencher({id: contaBancariaId, apelido: 'Conta bancária não encontrada'}));
        return () => {
            ativo = false;
        };
    }, [paymentMethod, open]);

    const handleChange = (field) => (event) => {
        const nextValue = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
        setValues((current) => ({...current, [field]: nextValue}));
    };

    const handleTypeChange = (tipo) => {
        setValues((current) => ({
            ...current,
            tipo,
            contaBancariaId: tipo === 'CARTAO' ? '' : current.contaBancariaId,
            contaBancaria: tipo === 'CARTAO' ? null : current.contaBancaria,
        }));
    };

    const handleBankAccountChange = (account) => {
        setValues((current) => ({
            ...current,
            contaBancariaId: account?.id || '',
            contaBancaria: account || null,
        }));
    };

    const validate = () => {
        const nextErrors = {};
        const requiresBankAccount = values.tipo === 'PIX' || values.tipo === 'BOLETO';

        if (!values.descricao.trim()) nextErrors.descricao = 'Nome é obrigatório';
        if (!values.codigo.trim()) nextErrors.codigo = 'Código é obrigatório';
        if (!values.tipo.trim()) nextErrors.tipo = 'Meio de pagamento é obrigatório';
        if (requiresBankAccount && !values.contaBancariaId) {
            nextErrors.contaBancariaId = 'Conta bancária é obrigatória para PIX e Boleto';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        const success = await onSave?.(values, paymentMethod?.id);
        if (success) onClose?.();
    };

    const requiresBankAccount = values.tipo === 'PIX' || values.tipo === 'BOLETO';
    const selectedBankAccount = values.contaBancaria || (values.contaBancariaId ? {id: values.contaBancariaId, apelido: 'Carregando conta bancária...'} : null);

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{sx: {borderRadius: 3, maxHeight: '90vh'}}}>
            <DialogTitle sx={{p: 0}}>
                <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1}}>
                    <Box>
                        <Typography sx={{fontSize: '1.5rem', color: '#111827', fontWeight: 700}}>
                            {paymentMethod ? 'Editar Forma de Pagamento' : 'Nova Forma de Pagamento'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                            Configure os dados do forma de pagamento
                        </Typography>
                    </Box>
                    <Button onClick={onClose} disabled={saving} sx={{minWidth: 0, p: 1, borderRadius: 2, color: '#6b7280'}}>
                        <X size={18} />
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{p: 3}}>
                <Stack spacing={4}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2.5, color: '#374151'}}>
                            <Building2 size={18} />
                            <Typography sx={sectionTitleSx}>Informações da Forma de Pagamento</Typography>
                        </Stack>

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'}, gap: 2}}>
                            <TextField
                                label="Nome da Forma de Pagamento"
                                value={values.descricao}
                                onChange={handleChange('descricao')}
                                error={Boolean(errors.descricao)}
                                helperText={errors.descricao}
                                placeholder="Ex: Forma de Pagamento do Brasil"
                                sx={{...inputSx, gridColumn: {md: 'span 2'}}}
                            />

                            <TextField
                                label="Código"
                                value={values.codigo}
                                onChange={(event) => setValues((current) => ({...current, codigo: event.target.value.toUpperCase()}))}
                                error={Boolean(errors.codigo)}
                                helperText={errors.codigo || 'Código único. A cobrança das faturas por boleto usa a forma com o código BOLETO ou PIX.'}
                                placeholder="Ex: 1"
                                sx={inputSx}
                            />

                            <Box sx={{gridColumn: {md: 'span 2'}}}>
                                <Typography variant="body2" sx={{color: '#374151', mb: 1}}>
                                    Meio de Pagamento <Box component="span" sx={{color: '#ef4444'}}>*</Box>
                                </Typography>
                                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'}, gap: 1.5}}>
                                    {paymentMethodTypes.map(({value, label, icon: Icon}) => {
                                        const selected = values.tipo === value;
                                        return (
                                            <Button
                                                key={value}
                                                type="button"
                                                onClick={() => handleTypeChange(value)}
                                                variant="outlined"
                                                sx={{
                                                    minHeight: 84,
                                                    borderWidth: 2,
                                                    borderColor: selected ? '#14b8a6' : '#d1d5db',
                                                    bgcolor: selected ? '#f0fdfa' : '#fff',
                                                    color: selected ? '#0f766e' : '#374151',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.75,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        borderWidth: 2,
                                                        borderColor: selected ? '#14b8a6' : '#9ca3af',
                                                        bgcolor: selected ? '#f0fdfa' : '#fff',
                                                    },
                                                }}
                                            >
                                                <Icon size={22} />
                                                <Typography variant="body2" sx={{fontWeight: 600}}>{label}</Typography>
                                            </Button>
                                        );
                                    })}
                                </Box>
                                {errors.tipo ? (
                                    <Typography variant="caption" sx={{display: 'block', color: '#dc2626', mt: 0.75}}>
                                        {errors.tipo}
                                    </Typography>
                                ) : null}
                            </Box>

                            {requiresBankAccount ? (
                                <Box sx={{gridColumn: {md: 'span 2'}}}>
                                    <RemoteSelect
                                        label="Conta Bancária"
                                        value={selectedBankAccount}
                                        onChange={handleBankAccountChange}
                                        fetchOptions={({query, page, pageSize}) => listFinanceiroContasBancarias({
                                            status: 'ATIVA',
                                            apelido: query || undefined,
                                            page,
                                            size: pageSize,
                                        })}
                                        getOptionValue={(option) => option.id}
                                        getOptionLabel={getBankAccountLabel}
                                        getOptionDescription={getBankAccountDescription}
                                        placeholder="Selecione uma conta bancária"
                                        searchPlaceholder="Buscar conta bancária..."
                                        emptyMessage="Nenhuma conta bancária ativa disponível"
                                        noResultsMessage="Nenhuma conta bancária encontrada"
                                        pageSize={10}
                                        initialPage={0}
                                        pageBase={0}
                                        error={errors.contaBancariaId}
                                        helperText={`Selecione a conta bancária que será utilizada para ${values.tipo}`}
                                        required
                                    />
                                </Box>
                            ) : (
                                <Box sx={{gridColumn: {md: 'span 2'}, p: 2, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe'}}>
                                    <Typography variant="body2" sx={{color: '#1d4ed8'}}>
                                        Cartão não requer vinculação com conta bancária.
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{gridColumn: {md: 'span 2'}, display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, border: '1px solid #d1d5db', borderRadius: 2, bgcolor: '#fff'}}>
                                <Switch checked={Boolean(values.ativo)} onChange={handleChange('ativo')} />
                                <Box>
                                    <Typography sx={{fontSize: 14, color: '#374151'}}>Forma de pagamento ativa</Typography>
                                    <Typography variant="caption" sx={{color: '#6b7280'}}>
                                        Formas inativas não estarão disponíveis para uso.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{display: 'flex', justifyContent: 'space-between', px: 3, py: 2.5, borderTop: '1px solid #e5e7eb'}}>
                <Stack direction="row" spacing={1.5}>
                    <Button onClick={onClose} disabled={saving} sx={{border: '1px solid #d1d5db', color: '#374151'}}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}}}>
                        {paymentMethod ? 'Salvar Alterações' : 'Cadastrar Forma de Pagamento'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

export default function FinanceiroPaymentMethodsTab({
    items,
    pagination,
    loading,
    saving,
    search,
    page,
    onChangeSearch,
    onChangePage,
    onSave,
    onDelete,
}) {
    const [editing, setEditing] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const openDialog = (account = null) => {
        setEditing(account);
        setDialogOpen(true);
    };

    return (
        <Stack spacing={3}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
                <TextField
                    value={search}
                    onChange={(event) => onChangeSearch?.(event.target.value)}
                    placeholder="Buscar por código ou nome da forma de pagamento..."
                    fullWidth
                    InputProps={{startAdornment: <Search size={18} color="#9ca3af" style={{marginRight: 8}} />}}
                />
                <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => openDialog()} sx={{bgcolor: '#0f766e', '&:hover': {bgcolor: '#115e59'}, whiteSpace: 'nowrap'}}>
                    Cadastrar forma de pagamento
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{borderRadius: 3, overflow: 'hidden', borderColor: '#e5e7eb'}}>
                <Table>
                    <TableHead sx={{bgcolor: '#f9fafb'}}>
                        <TableRow>
                            <TableCell>Forma de Pagamento</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!items.length ? (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography color="text.secondary" sx={{py: 3, textAlign: 'center'}}>
                                        Nenhuma forma de pagamento encontrada.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : items.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box sx={{width: 40, height: 40, borderRadius: 2, bgcolor: '#ccfbf1', color: '#0f766e', display: 'grid', placeItems: 'center'}}>
                                                <Building2 size={20} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{color: '#111827', fontWeight: 600}}>{item.descricao || '-'}</Typography>
                                                <Typography variant="body2" color="text.secondary">{item.codigo || '-'}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={() => openDialog(item)}>Editar</Button>
                                            <Button color="error" variant="outlined" startIcon={<Trash2 size={16} />} onClick={() => onDelete?.(item.id)} disabled={saving}>Excluir</Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Mostrando {(Number(page || 0) * 10) + (items.length ? 1 : 0)} a {Math.min((Number(page || 0) * 10) + items.length, pagination?.total || items.length)} de {pagination?.total || items.length} forma de pagamentos
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={!pagination?.has_previous} onClick={() => onChangePage?.(Math.max(0, Number(page || 0) - 1))} sx={{minWidth: 44, px: 1.5}}>
                        Anterior
                    </Button>
                    <Button variant="outlined" disabled={!pagination?.has_next} onClick={() => onChangePage?.(Number(page || 0) + 1)} sx={{minWidth: 44, px: 1.5}}>
                        Próxima
                    </Button>
                </Stack>
            </Stack>

            <PaymentMethodsDialog
                open={dialogOpen}
                paymentMethod={editing}
                saving={saving || loading}
                onClose={() => setDialogOpen(false)}
                onSave={onSave}
            />
        </Stack>
    );
}
